import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import type { VoteResults, GovernanceVoting } from '@/types/governance';
import { checkEarlyPassingConditions } from '@/utils/voteCalculations';
import { Vote, Info, ChevronDown, Zap } from 'lucide-react';
import SuiCoinIcon from '@/components/icons/sui-coin.png';

interface VoteTallyProps {
    results: VoteResults;
    voteStatus: 'not_started' | 'active' | 'ended';
    vote: GovernanceVoting;
    onScrollToDetails?: () => void;
}

export function VoteTally({ results, voteStatus, vote, onScrollToDetails }: VoteTallyProps) {
    const notVotedLabel = voteStatus === 'ended' ? "Did not vote" : "Have not voted";

    // Helper function to format percentage display
    const formatPercentage = (percentage: number): string => {
        const rounded = Number(percentage.toFixed(1));
        if (rounded === 0 && percentage > 0) {
            return "<0.1%";
        }
        return `${rounded.toFixed(1)}%`;
    };

    // Check if vote has started
    const currentTime = Date.now();
    const voteHasStarted = currentTime >= vote.start_timestamp_ms;

    // Determine passing status details
    const yesWinning = results.yesStake > results.noStake;
    const sufficientTurnout = results.turnoutPercentage > 50;
    const isPassing = yesWinning && sufficientTurnout;

    // Check early passing conditions
    const earlyPassCheck = checkEarlyPassingConditions(vote, results);
    const showEarlyPassInfo = voteHasStarted && voteStatus === 'active';

    return (
        <Card className='bg-[#C0E6FF]/10 backdrop-blur-md border border-white/20 rounded-lg shadow-lg'>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Vote className="h-5 w-5" />
                    Current Vote Results
                    {voteHasStarted && (
                        <Tooltip delayDuration={0}>
                            <TooltipTrigger asChild>
                                <span className={`text-sm px-2 py-1 rounded flex items-center gap-1 cursor-help ${isPassing ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                    {isPassing ? 'PASSING' : 'NOT PASSING'}
                                    <Info className="h-3 w-3" />
                                </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs z-[9999]">
                                <div className="space-y-2">
                                    <p className="font-medium">Vote Passing Requirements:</p>
                                    <div className="space-y-1 text-sm">
                                        <p>1. <strong>Yes votes must exceed No votes</strong> (by stake weight)</p>
                                        <p>2. <strong>Turnout must be {'>'} 50%</strong> of total stake</p>
                                        <p className="text-xs text-muted-foreground mt-2">
                                            Note: Turnout = Yes + No votes only. Abstain votes don't count toward turnout but are still recorded.
                                        </p>
                                    </div>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div>
                    <div className="flex justify-between mb-2">
                        <span className="font-medium text-green-600 dark:text-green-400">Yes</span>
                        <span className="font-mono">{formatPercentage(results.yesPercentage)}</span>
                    </div>
                    <Progress value={results.yesPercentage} className="h-3" />
                </div>

                <div>
                    <div className="flex justify-between mb-2">
                        <span className="font-medium text-red-600 dark:text-red-400">No</span>
                        <span className="font-mono">{formatPercentage(results.noPercentage)}</span>
                    </div>
                    <Progress value={results.noPercentage} className="h-3" />
                </div>

                <div>
                    <div className="flex justify-between mb-2">
                        <span className="font-medium text-blue-600 dark:text-blue-400">Abstain</span>
                        <span className="font-mono">{formatPercentage(results.abstainPercentage)}</span>
                    </div>
                    <Progress value={results.abstainPercentage} className="h-3" />
                </div>

                <div>
                    <div className="flex justify-between mb-2">
                        <span className="font-medium text-gray-600 dark:text-gray-400">{notVotedLabel}</span>
                        <span className="font-mono">{formatPercentage(results.notVotedPercentage)}</span>
                    </div>
                    <Progress value={results.notVotedPercentage} className="h-3" />
                </div>

                {voteHasStarted && (
                    <div className="pt-4 border-t border-border space-y-3">
                        {/* Passing Conditions */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className={`p-2 rounded border ${yesWinning ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'}`}>
                                <div className="font-medium">Yes {'>'} No</div>
                                <div className={yesWinning ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                    {yesWinning ? '✓ Met' : '✗ Not met'}
                                </div>
                            </div>
                            <div className={`p-2 rounded border ${sufficientTurnout ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'}`}>
                                <div className="font-medium">Turnout {'>'} 50%</div>
                                <div className={sufficientTurnout ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                                    {formatPercentage(results.turnoutPercentage)} ({sufficientTurnout ? '✓ Met' : '✗ Not met'})
                                </div>
                            </div>
                        </div>

                        {/* Early Passing Conditions - only show during active voting */}
                        {showEarlyPassInfo && (
                            <div className="border-t border-border pt-3">
                                <div className="flex items-center gap-2 mb-2">
                                    <Zap className="h-4 w-4 text-yellow-600" />
                                    <span className="font-medium text-sm">Early Passing Conditions</span>
                                    <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                            <Info className="h-3 w-3 cursor-help" />
                                        </TooltipTrigger>
                                        <TooltipContent className="max-w-sm z-[9999]">
                                            <div className="space-y-2">
                                                <p className="font-medium">Vote can pass early if:</p>
                                                <div className="space-y-1 text-sm">
                                                    <p>1. <strong>Minimum duration elapsed</strong> (48 hours since start)</p>
                                                    <p>2. <strong>50%+ participation</strong> (Yes + No votes)</p>
                                                    <p>3. <strong>Yes votes {'>'} (No + Unvoted)</strong> - guaranteed win</p>
                                                </div>
                                            </div>
                                        </TooltipContent>
                                    </Tooltip>
                                </div>

                                <div className={`p-3 rounded border text-sm ${earlyPassCheck.canPassEarly
                                    ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                                    : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950 dark:border-yellow-800'
                                    }`}>
                                    {earlyPassCheck.canPassEarly ? (
                                        <div className="flex items-center gap-2">
                                            <Zap className="h-4 w-4 text-green-600 dark:text-green-400" />
                                            <span className="font-medium text-green-600 dark:text-green-400">
                                                ✓ All early passing conditions met - Vote can pass now!
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="space-y-1">
                                            <div className="font-medium text-yellow-800 dark:text-yellow-200">
                                                Early passing not yet available
                                            </div>
                                            <div className="text-yellow-700 dark:text-yellow-300 text-xs">
                                                {earlyPassCheck.reason}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="text-sm text-muted-foreground">
                            Total Voting Stake: {(Number(results.totalVotingStake) / 1e9).toLocaleString()}{' '}
                            <img src={SuiCoinIcon} alt="SUI" className="inline-block h-3 w-3 ml-1 align-top mt-1" />
                        </div>
                    </div>
                )}

                {/* Show total voting stake outside of conditions section when vote hasn't started */}
                {!voteHasStarted && (
                    <div className="pt-4 border-t border-border">
                        <div className="text-sm text-muted-foreground">
                            Total Voting Stake: {(Number(results.totalVotingStake) / 1e9).toLocaleString()}{' '}
                            <img src={SuiCoinIcon} alt="SUI" className="inline-block h-3 w-3 ml-1 align-top mt-1" />
                        </div>
                    </div>
                )}

                {/* See more details button */}
                {onScrollToDetails && (
                    <div className="pt-4 border-t border-border">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onScrollToDetails}
                            className="w-full flex items-center gap-2 hover:bg-[#C0E6FF]/20 transition-colors"
                        >
                            See more details
                            <ChevronDown className="h-4 w-4" />
                        </Button>
                    </div>
                )}
            </CardContent>
        </Card>
    );
} 